import React, { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { useAppContext } from '../../context/AppContext';
import { processEventsForTimeline } from '../../services/apiService';
import { AlertCircle, CheckCircle, Info, AlertTriangle, Clock, Zap, Wrench, Cpu, Ticket, X } from 'lucide-react';

const EventHistoryTab: React.FC = () => {
  const { state, events, loading, error, entities, concepts } = useAppContext();
  
  // Filter state
  const [entityTypeFilter, setEntityTypeFilter] = useState<string | null>(null);
  const [conceptFilter, setConceptFilter] = useState<string | null>(null);

  const filteredEvents = useMemo(() => {
    let filtered = [...events];

    // Filter by selected components (using entity_id)
    if (state.selectedComponents.length > 0 && entities.length > 0) {
      // Create map of entity names to IDs for filtering (handle both old and new field names)
      const entityMap = new Map(entities.map(e => [e.entity_name || e.name, e.entity_id || e.id]));
      const selectedEntityIds = state.selectedComponents
        .map(name => entityMap.get(name))
        .filter(Boolean);
      
      if (selectedEntityIds.length > 0) {
        filtered = filtered.filter(event => 
          !event.entity_id || selectedEntityIds.includes(event.entity_id)
        );
      }
    }

    // Filter by entity type (from tag clicks)
    if (entityTypeFilter) {
      filtered = filtered.filter(event => 
        getEntityDetails(event).some(entity => entity.type === entityTypeFilter)
      );
    }

    // Filter by concept (from tag clicks)
    if (conceptFilter) {
      filtered = filtered.filter(event => 
        getTagDetails(event).some(tag => tag.name === conceptFilter)
      );
    }

    // Filter by time range
    if (state.filterCriteria.timeRange) {
      const { start, end } = state.filterCriteria.timeRange;
      filtered = filtered.filter(event => {
        const eventTime = new Date(event.event_date || event.event_timestamp || '');
        return eventTime >= start && eventTime <= end;
      });
    }

    // Filter by event type
    if (state.filterCriteria.eventSources?.length) {
      filtered = filtered.filter(event => 
        state.filterCriteria.eventSources!.includes(event.event_type)
      );
    }

    return filtered.sort((a, b) => 
      new Date(b.event_date || b.event_timestamp || '').getTime() - 
      new Date(a.event_date || a.event_timestamp || '').getTime()
    );
  }, [events, state.selectedComponents, state.filterCriteria, entities, entityTypeFilter, conceptFilter]);

  const getEventIcon = (type: string, severity: string) => {
    // Determine icon by severity first, then type
    switch (severity.toLowerCase()) {
      case 'critical':
      case 'error':
        return <AlertCircle className="w-5 h-5 text-accent-red" />;
      case 'warning':
      case 'medium':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'info':
      case 'low':
        return <Info className="w-5 h-5 text-engramiq-green" />;
      case 'success':
        return <CheckCircle className="w-5 h-5 text-engramiq-green" />;
      default:
        // Fall back to type-based icons
        switch (type.toLowerCase()) {
          case 'maintenance':
          case 'scheduled':
            return <Wrench className="w-5 h-5 text-engramiq-green" />;
          case 'performance':
          case 'monitoring':
            return <Cpu className="w-5 h-5 text-blue-500" />;
          case 'electrical':
          case 'power':
            return <Zap className="w-5 h-5 text-yellow-500" />;
          default:
            return <Clock className="w-5 h-5 text-engramiq-bright-grey" />;
        }
    }
  };
  
  const getEventSourceColor = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'critical':
      case 'error':
        return '#ef5350';
      case 'warning':
      case 'medium':
        return '#F59E0B';
      case 'info':
      case 'low':
        return '#10B981';
      case 'success':
        return '#10B981';
      default:
        return '#6B7280';
    }
  };

  // Helper function to extract entity names from affected_entities
  const getEntityNames = (event: any): string[] => {
    if (!event.affected_entities || !Array.isArray(event.affected_entities)) return [];
    return event.affected_entities.map((entity: any) => 
      entity.entity_name || entity.name || 'Unknown Entity'
    ).filter(Boolean);
  };

  // Helper function to extract tag names from tags
  const getTagNames = (event: any): string[] => {
    if (!event.tags || !Array.isArray(event.tags)) return [];
    return event.tags.map((tag: any) => 
      tag.tag_name || tag.name || 'Unknown Tag'
    ).filter(Boolean);
  };

  // Helper function to get entity details
  const getEntityDetails = (event: any): Array<{name: string, type: string, id: string}> => {
    if (!event.affected_entities || !Array.isArray(event.affected_entities)) return [];
    return event.affected_entities.map((entity: any) => ({
      name: entity.entity_name || entity.name || 'Unknown Entity',
      type: entity.entity_type || 'Unknown Type',
      id: entity.entity_id || entity.id || ''
    }));
  };

  // Helper function to get tag details
  const getTagDetails = (event: any): Array<{name: string, description: string}> => {
    if (!event.tags || !Array.isArray(event.tags)) return [];
    return event.tags.map((tag: any) => ({
      name: tag.tag_name || tag.name || 'Unknown Tag',
      description: tag.description || ''
    }));
  };

  // Click handlers for filtering
  const handleEntityTypeClick = (entityType: string) => {
    setEntityTypeFilter(entityTypeFilter === entityType ? null : entityType);
    setConceptFilter(null); // Clear concept filter when selecting entity type
  };

  const handleConceptClick = (conceptName: string) => {
    setConceptFilter(conceptFilter === conceptName ? null : conceptName);
    setEntityTypeFilter(null); // Clear entity type filter when selecting concept
  };

  const clearAllFilters = () => {
    setEntityTypeFilter(null);
    setConceptFilter(null);
  };

  const handleGenerateTicket = (event: any) => {
    // Dummy function - placeholder for ticket generation
    // TODO: Implement actual ticket generation logic
    alert(`Ticket generation requested for: ${event.title}`);
  };

  const groupedEvents = useMemo(() => {
    const groups: { [key: string]: typeof filteredEvents } = {};
    
    filteredEvents.forEach(event => {
      // Group by concept and category if available, otherwise by date
      const groupKey = event.concept_name 
        ? `${event.concept_name}${event.category_name ? ` (${event.category_name})` : ''}`
        : format(new Date(event.event_date || event.event_timestamp || ''), 'yyyy-MM-dd');
      
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(event);
    });

    return groups;
  }, [filteredEvents]);

  // Show loading state
  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-engramiq-dark-blue">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-engramiq-green border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-engramiq-light-grey font-medium">Loading...</div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-engramiq-dark-blue">
        <div className="text-center p-8">
          <div className="text-accent-red text-xl mb-2">⚠️</div>
          <div className="text-engramiq-light-grey font-medium mb-2">Failed to load events from database</div>
          <div className="text-engramiq-bright-grey text-sm">{error}</div>
        </div>
      </div>
    );
  }

  // Show no events state
  if (!events.length) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-engramiq-dark-blue">
        <div className="text-center p-8">
          <div className="text-engramiq-bright-grey text-lg mb-2">📅</div>
          <div className="text-engramiq-light-grey font-medium mb-2">No Events Found</div>
          <div className="text-engramiq-bright-grey text-sm">
            No events recorded for the selected site and time period
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-engramiq-dark-blue overflow-auto">
      <div className="p-4">
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xl font-semibold text-engramiq-light-grey">Event Timeline</h2>
            <div className="flex gap-2 text-xs text-engramiq-bright-grey">
              <span>{filteredEvents.length} events</span>
              {concepts.length > 0 && (
                <span>• {concepts.length} concepts</span>
              )}
            </div>
          </div>
          
          {/* Active Filters */}
          {(entityTypeFilter || conceptFilter) && (
            <div className="mb-3 p-2 bg-surface-800/30 rounded border border-engramiq-green/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-engramiq-light-grey">Active Filters:</span>
                  {entityTypeFilter && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-500/20 text-blue-300 border border-blue-500/30">
                      🔧 Entity Type: {entityTypeFilter}
                    </span>
                  )}
                  {conceptFilter && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-engramiq-green/20 text-engramiq-green border border-engramiq-green/30">
                      🏷️ Concept: {conceptFilter}
                    </span>
                  )}
                </div>
                <button
                  onClick={clearAllFilters}
                  className="flex items-center gap-1 px-2 py-1 text-xs text-engramiq-bright-grey hover:text-engramiq-light-grey transition-colors"
                >
                  <X className="w-3 h-3" />
                  Clear
                </button>
              </div>
            </div>
          )}

          {/* Tags Summary */}
          {filteredEvents.length > 0 && (
            <div className="flex items-center gap-4 text-xs text-engramiq-bright-grey">
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-blue-500/50"></span>
                <span>{filteredEvents.filter(e => getEntityDetails(e).length > 0).length} events with entities</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-engramiq-green/50"></span>
                <span>{filteredEvents.filter(e => getTagDetails(e).length > 0).length} events with concept tags</span>
              </div>
              {filteredEvents.filter(e => e.duration_minutes && e.duration_minutes > 0).length > 0 && (
                <div className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-yellow-500/50"></span>
                  <span>{filteredEvents.filter(e => e.duration_minutes && e.duration_minutes > 0).length} events with downtime</span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="relative">
          <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-engramiq-green/20"></div>
          
          {Object.entries(groupedEvents).map(([groupName, groupEvents]) => (
            <div key={groupName} className="mb-6">
              <div className="flex items-center mb-3">
                <div className="bg-surface-800/50 px-3 py-1 rounded-full text-sm font-semibold text-engramiq-light-grey border border-engramiq-green/20">
                  {groupName.includes('(') ? groupName : format(new Date(groupName), 'MMMM d, yyyy')}
                  <span className="ml-2 text-xs text-engramiq-bright-grey">({groupEvents.length})</span>
                </div>
              </div>
              
              {groupEvents.map((event) => (
                <div key={event.event_id} className="flex gap-4 mb-4 relative">
                  <div className="flex-shrink-0 w-16 text-right">
                    <span className="text-xs text-engramiq-bright-grey">
                      {format(new Date(event.event_date || event.event_timestamp || ''), 'HH:mm')}
                    </span>
                  </div>
                  
                  <div className="flex-shrink-0 relative">
                    <div 
                      className="w-4 h-4 rounded-full border-2 border-engramiq-dark-blue"
                      style={{ backgroundColor: getEventSourceColor(event.severity) }}
                    />
                  </div>
                  
                  <div className="flex-grow">
                    <div className="bg-surface-800/30 rounded-lg p-4 border border-engramiq-green/20 hover:border-engramiq-green/40 transition-colors">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {getEventIcon(event.event_type, event.severity)}
                          <h3 className="font-semibold text-engramiq-light-grey">{event.title || event.event_name}</h3>
                        </div>
                        <div className="flex items-center gap-2">
                          <span 
                            className="text-xs px-2 py-1 rounded-full text-white"
                            style={{ backgroundColor: getEventSourceColor(event.severity) }}
                          >
                            {event.severity}
                          </span>
                          <button
                            onClick={() => handleGenerateTicket(event)}
                            className="flex items-center gap-1 px-2 py-1 text-xs bg-engramiq-green/20 text-engramiq-green border border-engramiq-green/30 rounded hover:bg-engramiq-green/30 transition-colors"
                            title="Generate support ticket for this event"
                          >
                            <Ticket className="w-3 h-3" />
                            Generate Ticket
                          </button>
                        </div>
                      </div>
                      
                      <p className="text-sm text-engramiq-bright-grey mb-3">{event.description}</p>
                      
                      {/* Tags Section */}
                      {((event.affected_entities && Array.isArray(event.affected_entities) && event.affected_entities.length > 0) ||
                        (event.tags && Array.isArray(event.tags) && event.tags.length > 0)) && (
                        <div className="mb-3 p-2 bg-surface-800/20 rounded border border-engramiq-green/10">
                          
                          {/* Related Entities Tags */}
                          {getEntityDetails(event).length > 0 && (
                            <div className="mb-2">
                              <div className="text-xs font-medium text-engramiq-light-grey mb-1">Related Entities:</div>
                              <div className="flex flex-wrap gap-1">
                                {getEntityDetails(event).map((entity, index: number) => (
                                  <button
                                    key={`entity-${entity.id || index}`}
                                    onClick={() => handleEntityTypeClick(entity.type)}
                                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs border transition-colors cursor-pointer ${
                                      entityTypeFilter === entity.type
                                        ? 'bg-blue-500/40 text-blue-200 border-blue-400/50'
                                        : 'bg-blue-500/20 text-blue-300 border-blue-500/30 hover:bg-blue-500/30'
                                    }`}
                                    title={`Click to filter by entity type: ${entity.type}`}
                                  >
                                    🔧 {entity.name}
                                    {entity.type && (
                                      <span className="ml-1 text-blue-400/70 text-[10px]">
                                        [{entity.type}]
                                      </span>
                                    )}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Concept Tags */}
                          {getTagDetails(event).length > 0 && (
                            <div className={getEntityDetails(event).length > 0 ? "mt-2" : ""}>
                              <div className="text-xs font-medium text-engramiq-light-grey mb-1">Concept Tags:</div>
                              <div className="flex flex-wrap gap-1">
                                {getTagDetails(event).map((tag, index: number) => (
                                  <button
                                    key={`tag-${tag.name || index}`}
                                    onClick={() => handleConceptClick(tag.name)}
                                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs border transition-colors cursor-pointer ${
                                      conceptFilter === tag.name
                                        ? 'bg-engramiq-green/40 text-engramiq-light-grey border-engramiq-green/50'
                                        : 'bg-engramiq-green/20 text-engramiq-green border-engramiq-green/30 hover:bg-engramiq-green/30'
                                    }`}
                                    title={`Click to filter by concept: ${tag.name}${tag.description ? `\nDescription: ${tag.description}` : ''}`}
                                  >
                                    🏷️ {tag.name}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      
                      <div className="flex items-center gap-4 text-xs text-engramiq-bright-grey">
                        <span>Type: {event.event_type}</span>
                        {event.entity_name && (
                          <span>Entity: {event.entity_name}</span>
                        )}
                        {event.concept_name && (
                          <span>Concept: {event.concept_name}</span>
                        )}
                        {event.duration_minutes && (
                          <span>Duration: {event.duration_minutes}m</span>
                        )}
                        {event.severity && (
                          <span>Severity: {event.severity}</span>
                        )}
                      </div>

                      {event.metadata && Object.keys(event.metadata).length > 0 && (
                        <div className="mt-2 pt-2 border-t border-engramiq-green/10">
                          <div className="text-xs text-engramiq-bright-grey">
                            {Object.keys(event.metadata).length} metadata properties
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default EventHistoryTab;